import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { Content } from "~/components/app/content";
import { trpc } from "~/libs/trpc.lib";
import { BoardPage } from "~/pages/board/board.page";
import {
  BoardSessionProvider,
  type BoardViewMode,
} from "~/pages/board/board-session";
import { isSnapshotOrigin } from "~/stores/board-snapshot/snapshot-origins";
import { boards$ } from "~/stores/boards";
import { activeOrganization$ } from "~/stores/organizations";

/**
 * Resolved access to a `/boards/{id}` URL. Stored in the route context so
 * the component tree can pick the right shell. Mirrors
 * [`BoardViewMode`](~/pages/board/board-session) one-for-one.
 *
 * AI features are gated to `app` only — the chat panel is mounted under
 * `<App>` and never inside `<GuestBoardLayout>`.
 */
export type ViewMode = BoardViewMode;

/**
 * Top-level `/boards/$boardId` route.
 *
 * Replaces the previous `/_app/boards/$boardId` route so internet-public
 * boards and Phase 6 board guests can render the same URL without going
 * through the org-onboarding gate.
 */
export const Route = createFileRoute("/boards/$boardId")({
  validateSearch: z.object({
    blockId: z.string().optional(),
  }),
  // Public links should not be indexed by search engines: the snapshot is
  // user-shared, not public web content, and may surface unexpectedly via
  // SEO. The read-only viewer also dumps no human-readable content into
  // the initial HTML (it's a SPA), but the meta tag is cheap insurance.
  head: () => ({
    meta: [{ name: "robots", content: "noindex,nofollow" }],
  }),
  beforeLoad: async ({ context, params, location }) => {
    // Fast path: when the board is a genuine in-app board for the current
    // session, resolve `app` access synchronously and skip the blocking
    // `resolveAccess` round-trip that would otherwise gate every in-app
    // board navigation. The guard is deliberately strict because `boards$`
    // is shared with the read-only viewer:
    //   - `context.user` must exist — anonymous public viewers fall through
    //     to `resolveAccess` (never redirected to `/login` here).
    //   - the row must not be snapshot-origin — the read-only viewer
    //     hydrates public boards into this same store while mounted.
    //   - the row's org must match the active org — a stale persisted
    //     snapshot row (IndexedDB survives a refresh, but the in-memory
    //     origin tag does not) would otherwise be misread as an app board.
    // Boards failing any check (guests, public links, cross-org deep links,
    // cold loads) fall through to the network check.
    const localBoard = boards$[params.boardId];
    if (
      context.user &&
      localBoard?.id.get() &&
      !isSnapshotOrigin(params.boardId) &&
      localBoard.organizationId.get() === activeOrganization$.id.get()
    ) {
      if (context.user.onboarding && context.user.onboarding !== "done") {
        throw redirect({ to: "/onboard", replace: true });
      }
      const org = activeOrganization$.get();
      if (org && org.onboarding !== "done") {
        throw redirect({ to: "/onboard", replace: true });
      }
      return { viewMode: "app" as const };
    }

    const access = await trpc.publicBoards.resolveAccess.query({
      boardId: params.boardId,
    });

    if (access.kind === "not-found") {
      throw notFound();
    }

    if (access.kind === "login-required") {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
        replace: true,
      });
    }

    if (access.kind === "app") {
      // Replicates `_app/route.tsx` gating since this route lives at the
      // top level. Users mid-onboarding are bounced through `/onboard` so
      // the workspace wizard isn't side-stepped via a deep board link.
      if (!context.user) {
        throw redirect({ to: "/login", replace: true });
      }
      if (context.user.onboarding && context.user.onboarding !== "done") {
        throw redirect({ to: "/onboard", replace: true });
      }
      const org = activeOrganization$.get();
      if (org?.id === access.organizationId && org.onboarding !== "done") {
        throw redirect({ to: "/onboard", replace: true });
      }
      return { viewMode: "app" as const };
    }

    if (access.kind === "readonly-member") {
      return { viewMode: "readonly-member" as const };
    }

    return { viewMode: "readonly-public" as const };
  },
  component: BoardRouteComponent,
  // `notFound()` is thrown in `beforeLoad`, so the match carries no
  // `viewMode`. `<AppShell>` resolves a board match without `viewMode` to the
  // `guest` chrome, so this only renders the inner body to avoid double-wrapping
  // `<GuestBoardLayout>`.
  notFoundComponent: () => (
    <Content.Body>
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="font-medium text-sm">Board not found</p>
        <p className="text-muted-foreground text-xs">
          This board doesn't exist or hasn't been shared.
        </p>
      </div>
    </Content.Body>
  ),
});

/**
 * Threads the resolved session through `BoardSessionProvider` so every leaf
 * inside `BoardPage` and the cross-cutting chrome (`TopBar`, `PageTitle`)
 * reads the same capability bag.
 *
 * Chrome selection (the `<App>` shell vs `<GuestBoardLayout>`) is owned by
 * `<AppShell>` in `__root.tsx`, which reads this route's `viewMode` from the
 * match context — keeping the `<App>` instance shared with `/_app/*` routes so
 * navigating between datasets and an app-mode board never remounts it.
 */
function BoardRouteComponent() {
  const { viewMode } = Route.useRouteContext();
  const { boardId } = Route.useParams();

  return (
    <BoardSessionProvider boardId={boardId} viewMode={viewMode}>
      <BoardPage />
    </BoardSessionProvider>
  );
}
